import React, { useState } from 'react'
import { LogEntry, ExceptionInfo } from '../types'
import { formatLogMessage } from '../services/formatLogMessage'
import { getEntityRef } from '../services/entityRef'
import useStyles from '../styles'
import JsonView from './JsonView'
import CopyButton from './CopyButton'
import EntityBlock from './EntityBlock'

type CorrelationField = 'traceId' | 'requestId'

interface LogEntryDetailProps {
  entry: LogEntry
  onCorrelate?: (field: CorrelationField, value: string) => void
}

const CorrelateButton: React.FC<{ onClick: () => void; label: string }> = ({
  onClick,
  label,
}) => {
  const classes = useStyles()
  return (
    <button
      type='button'
      className={classes.traceFilterButton}
      onClick={onClick}
      title={label}
      aria-label={label}
    >
      <span
        className='material-symbols-outlined'
        style={{ fontSize: 14 }}
      >
        hub
      </span>
      Correlate
    </button>
  )
}

function getPayloadField(entry: LogEntry, key: string): string {
  const val = entry.metadata.payload?.[key]
  return typeof val === 'string' ? val : ''
}

function messageBlockClass(
  classes: ReturnType<typeof useStyles>,
  level: LogEntry['level'],
): string {
  switch (level) {
    case 'WARN':
      return `${classes.messageBlock} ${classes.messageBlockWarn}`
    case 'INFO':
      return `${classes.messageBlock} ${classes.messageBlockInfo}`
    case 'DEBUG':
      return `${classes.messageBlock} ${classes.messageBlockDebug}`
    default:
      return classes.messageBlock
  }
}

function getExceptions(entry: LogEntry): ExceptionInfo[] {
  const raw = entry.metadata.payload?.exceptions
  if (!Array.isArray(raw)) return []
  return raw as ExceptionInfo[]
}

const LogEntryDetail: React.FC<LogEntryDetailProps> = ({
  entry,
  onCorrelate,
}) => {
  const classes = useStyles()
  const [showRaw, setShowRaw] = useState(false)

  const message = formatLogMessage(entry).text
  const messageTemplate = getPayloadField(entry, 'messageTemplate')
  const exceptions = getExceptions(entry)
  const traceId = entry.metadata.traceId ?? ''
  const requestId = entry.metadata.requestId ?? ''
  const entityRef = getEntityRef(entry)

  const payload = entry.metadata.payload

  return (
    <div
      className={`${classes.logDetail} ${
        entry.level === 'WARN'
          ? classes.logDetailWarn
          : entry.level !== 'ERROR'
            ? classes.logDetailNonError
            : ''
      }`}
    >
      <div className={classes.detailGrid}>
        {requestId !== '' && (
          <div>
            <div className={classes.detailLabel}>Request ID</div>
            <div className={classes.detailValueRow}>
              <span className={classes.detailValue}>{requestId}</span>
              <CopyButton
                value={requestId}
                label='Copy Request ID'
              />
              {onCorrelate != null && (
                <CorrelateButton
                  label='Show all logs with this Request ID'
                  onClick={() => onCorrelate('requestId', requestId)}
                />
              )}
            </div>
          </div>
        )}
        {traceId !== '' && (
          <div>
            <div className={classes.detailLabel}>Trace ID</div>
            <div className={classes.detailValueRow}>
              <span className={classes.detailValue}>{traceId}</span>
              <CopyButton
                value={traceId}
                label='Copy Trace ID'
              />
              {onCorrelate != null && (
                <CorrelateButton
                  label='Show all logs with this Trace ID'
                  onClick={() => onCorrelate('traceId', traceId)}
                />
              )}
            </div>
          </div>
        )}
        {entityRef != null && <EntityBlock entityRef={entityRef} />}
        {entry.audit != null && (
          <div>
            <div className={classes.detailLabel}>State</div>
            <div className={classes.detailValue}>{entry.audit.state}</div>
          </div>
        )}
        {entry.audit != null && (
          <div>
            <div className={classes.detailLabel}>Result</div>
            <div className={classes.detailValue}>{entry.audit.outcome}</div>
          </div>
        )}
        {entry.audit?.target != null && entry.audit.target !== '' && (
          <div>
            <div className={classes.detailLabel}>Target</div>
            <div className={classes.detailValue}>{entry.audit.target}</div>
          </div>
        )}
        {entry.metadata.userId != null && entry.metadata.userId !== '' && (
          <div>
            <div className={classes.detailLabel}>User ID</div>
            <div className={classes.detailValue}>{entry.metadata.userId}</div>
          </div>
        )}
        {entry.user != null &&
          entry.user !== '' &&
          (entry.metadata.userId == null || entry.metadata.userId === '') && (
            <div>
              <div className={classes.detailLabel}>User</div>
              <div className={classes.detailValue}>{entry.user}</div>
            </div>
          )}
        {messageTemplate !== '' && (
          <div>
            <div className={classes.detailLabel}>Message Template</div>
            <div className={classes.detailValue}>{messageTemplate}</div>
          </div>
        )}
      </div>

      {entry.level !== 'ERROR' && message !== '' && (
        <div className={messageBlockClass(classes, entry.level)}>{message}</div>
      )}

      {exceptions.length > 0 &&
        exceptions.map((ex, i) => (
          <div
            key={`${ex.ClassName ?? 'ex'}-${ex.Message ?? ''}-${i}`}
            className={classes.exceptionBlock}
          >
            <div className={classes.exceptionClass}>
              {ex.ClassName ?? 'Unknown Exception'}
            </div>
            <div className={classes.exceptionMessage}>
              {ex.Message ?? '(no message)'}
            </div>
          </div>
        ))}

      {payload !== undefined && payload !== null && (
        <div>
          <div className={classes.payloadHeader}>
            <span className={classes.payloadTitle}>Payload</span>
            <div className={classes.payloadActions}>
              <CopyButton
                value={JSON.stringify(payload, null, 2)}
                label='Copy payload JSON'
              />
              <button
                type='button'
                className={classes.payloadToggle}
                onClick={() => setShowRaw(prev => !prev)}
              >
                <span
                  className='material-symbols-outlined'
                  style={{ fontSize: 14 }}
                >
                  {showRaw ? 'account_tree' : 'data_object'}
                </span>
                {showRaw ? 'Pretty' : 'Raw JSON'}
              </button>
            </div>
          </div>
          {showRaw ? (
            <div className={classes.detailPayload}>
              {JSON.stringify(payload, null, 2)}
            </div>
          ) : (
            <JsonView data={payload} />
          )}
        </div>
      )}

      {entry.audit?.metadata != null && (
        <div>
          <div className={classes.payloadHeader}>
            <span className={classes.payloadTitle}>Metadata</span>
            <CopyButton
              value={JSON.stringify(entry.audit.metadata, null, 2)}
              label='Copy metadata JSON'
            />
          </div>
          <JsonView data={entry.audit.metadata} />
        </div>
      )}
    </div>
  )
}

export default LogEntryDetail
